import { type ComponentType, useEffect, useRef, useState } from 'react'

import type {
  ReadingPercentageProps,
  WithReadingPercentageProps,
} from './interface'

const withReadingPercentage = <P extends Record<any, any>>(
  WrappedComponent: ComponentType<P & ReadingPercentageProps>
) => {
  const WithReadingPercentage = (props: P & WithReadingPercentageProps) => {
    const {
      disableWayPoint = true,
      contentDebounceTime,
      handlePercentageChange,
      readingPercentage,
    } = props

    const contentRef = useRef<HTMLDivElement | null>(null)
    const timeOutRef = useRef<number | null>(null)
    const [percentage, setReadingPercentage] = useState(0)

    useEffect(() => {
      setTimeout(() => {
        if (readingPercentage && contentRef.current) {
          const { clientHeight } = document.documentElement
          const contentHeight = contentRef.current.offsetHeight

          const contentTop = contentRef.current.offsetTop
          const scrollPosition =
            ((contentHeight - clientHeight) * readingPercentage) / 100

          window.scrollTo({
            top: scrollPosition + contentTop,
            behavior: 'smooth',
          })
        }
      }, 500)
    }, [readingPercentage])

    useEffect(() => {
      const handleScroll = () => {
        const execute = () => {
          if (!disableWayPoint && contentRef.current) {
            const { scrollTop, clientHeight } = document.documentElement

            const contentTop = contentRef.current.offsetTop
            const contentHeight = contentRef.current.offsetHeight

            if (
              scrollTop + clientHeight >= contentTop &&
              scrollTop <= contentTop + contentHeight
            ) {
              const scrollPosition = Math.max(scrollTop - contentTop, 0)
              const roundPercentage = Math.round(
                (scrollPosition / (contentHeight - clientHeight)) * 100
              )
              const changePercentage = Math.min(
                Math.max(roundPercentage, 0),
                100
              )

              setReadingPercentage(changePercentage)
              handlePercentageChange?.(changePercentage)
            }
          }
        }

        if (disableWayPoint) {
          timeOutRef.current && clearTimeout(timeOutRef.current)
          return
        }

        if (contentDebounceTime) {
          timeOutRef.current && clearTimeout(timeOutRef.current)
          timeOutRef.current = window.setTimeout(execute, contentDebounceTime)
        } else {
          execute()
        }
      }

      window.addEventListener('scroll', handleScroll)
      return () => window.removeEventListener('scroll', handleScroll)

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const newProps = {
      ...props,
      readingPercentage: percentage,
    }

    return (
      <div ref={contentRef}>
        <WrappedComponent {...newProps} />
      </div>
    )
  }

  return WithReadingPercentage
}

export default withReadingPercentage
